import React, { useMemo } from 'react';
import { FaArrowDown, FaArrowUp, FaMinus } from 'react-icons/fa';
import {
  getKpiTrendColor,
  resolveKpiTrendKey,
  resolveKpiWidgetStyles,
} from '../../utils/kpiWidgetData';
import './css/KpiWidget.css';

function trendIconStyle(color) {
  return color ? { color, fill: color } : undefined;
}

function TrendIcon({ trend, color }) {
  const iconStyle = trendIconStyle(color);
  if (trend === 'UP') return <FaArrowUp className="kpi-trend-icon" style={iconStyle} aria-hidden />;
  if (trend === 'DOWN') return <FaArrowDown className="kpi-trend-icon" style={iconStyle} aria-hidden />;
  return <FaMinus className="kpi-trend-icon" style={iconStyle} aria-hidden />;
}

const kpiCenterLayout = (color) => ({
  textAlign: 'center',
  alignItems: 'center',
  width: '100%',
  ...(color ? { color } : null),
});

/** Layout-only centering (no color). Kept for any stale references. */
const KPI_CENTER_STYLE = kpiCenterLayout();

const KpiWidget = ({
  title,
  dataset,
  loading = false,
  error = null,
  compact = false,
  widgetConfig,
}) => {
  const configKey =
    widgetConfig == null
      ? ''
      : typeof widgetConfig === 'string'
        ? widgetConfig
        : JSON.stringify(widgetConfig);
  const styles = useMemo(
    () => resolveKpiWidgetStyles(widgetConfig, dataset?._metadata),
    [configKey, widgetConfig, dataset?._metadata]
  );

  const className = [
    'kpi-widget',
    'kpi-widget-align-center',
    compact && 'kpi-widget-compact',
  ]
    .filter(Boolean)
    .join(' ');

  const tileStyle = {
    backgroundColor: styles.backgroundColor,
    '--kpi-title-color': styles.titleColor,
    '--kpi-value-color': styles.valueColor,
    '--kpi-positive-color': styles.positiveColor,
    '--kpi-negative-color': styles.negativeColor,
    textAlign: 'center',
    alignItems: 'center',
  };

  if (loading) {
    return (
      <div className={`${className} kpi-widget-loading`} style={tileStyle}>
        <div className="kpi-loading-spinner" />
        <p>Loading KPI...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className} kpi-widget-error`} style={tileStyle} role="alert">
        <p className="kpi-error-text">{error}</p>
      </div>
    );
  }

  if (!dataset) {
    return (
      <div className={`${className} kpi-widget-empty`} style={tileStyle}>
        <p>No KPI data</p>
      </div>
    );
  }

  const {
    formatted_value: formattedValue,
    change_percent: changePercent,
    trend,
    show_trend: showTrend = true,
    previous_formatted_value: previousFormatted,
  } = dataset;

  const displayValue =
    formattedValue != null && formattedValue !== ''
      ? formattedValue
      : dataset.value != null
        ? String(dataset.value)
        : '—';

  const showComparison =
    showTrend && changePercent != null && Number.isFinite(Number(changePercent));

  const trendKey = resolveKpiTrendKey(trend, changePercent);
  const trendColor = getKpiTrendColor(styles, trendKey);
  const trendTextStyle = { color: trendColor, fill: trendColor };

  return (
    <div className={className} style={tileStyle}>
      {title ? (
        <div className="kpi-widget-title" style={kpiCenterLayout(styles.titleColor)}>
          {title}
        </div>
      ) : null}
      <div className="kpi-widget-body" style={KPI_CENTER_STYLE}>
        <div
          className="kpi-main-value"
          title={String(dataset.value ?? '')}
          style={kpiCenterLayout(styles.valueColor)}
        >
          {displayValue}
        </div>
        {showComparison && (
          <div
            className={`kpi-trend-row kpi-trend-${trendKey.toLowerCase()}`}
            style={{
              ...trendTextStyle,
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <TrendIcon trend={trendKey} color={trendColor} />
            <span className="kpi-trend-pct" style={{ color: trendColor }}>
              {changePercent > 0 ? '+' : ''}
              {changePercent}%
            </span>
            {previousFormatted && <span className="kpi-previous-hint">vs {previousFormatted}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default KpiWidget;
